import type { FileStore } from "./fileStore";
import type { NativeAudio } from "./nativeAudio";
import { createMMKV } from "react-native-mmkv";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { AppState, Platform } from "react-native";
import { signOut } from "firebase/auth";
import { auth } from "@utils/firebase";
import config from "@utils/config";
import { getCountryCode } from "@utils/region";
import { internetStatus } from "@utils/internetStatus";
import { createWebDatabase } from "@sqlite/webDatabase";
import { nativeClock } from "../components/Checkout/nativePorts";
import { createPurchaseTransport, createSellerLogin } from "../components/Checkout/vendorTransport";
import { httpTransport } from "../data/core/httpTransport";
import type { DevicePorts, MobileDatabase } from "./ports";
import { nativeAccountCleanup } from "./nativeAccountCleanup";

// Web playback uses Shaka; reaching the native adapter is a wiring error.
function nativeAudioUnavailable(): never {
  throw new Error("Native audio is unavailable on web");
}
const audio: NativeAudio = {
  configure: nativeAudioUnavailable,
  play: nativeAudioUnavailable,
  pause: nativeAudioUnavailable,
  resume: nativeAudioUnavailable,
  stop: nativeAudioUnavailable,
  clear: nativeAudioUnavailable,
  seekTo: nativeAudioUnavailable,
  getTimings: nativeAudioUnavailable,
  addEventListener: nativeAudioUnavailable,
};

function nativeFilesUnavailable(): never {
  throw new Error("Native downloads are unavailable on web");
}
const files: FileStore = {
  downloadDirectory: "",
  File: class {
    readonly exists = false;
    delete = nativeFilesUnavailable;
  },
  Directory: class {
    readonly exists = false;
    create = nativeFilesUnavailable;
    delete = nativeFilesUnavailable;
  },
  createDownloadResumable: nativeFilesUnavailable,
};

export function nativePorts(): DevicePorts {
  return {
    audio,
    files,
    databaseChanges: { subscribe: () => () => {} },
    clock: nativeClock,
    sellerLogin: createSellerLogin,
    purchaseTransport: createPurchaseTransport,
    api: httpTransport({ url: `${config.API_URL}/api/trpc/v3` }),
    db: createWebDatabase() as unknown as MobileDatabase,
    sqlite: null,
    kv: createMMKV(),
    asyncStorage: AsyncStorage,
    accountCleanup: nativeAccountCleanup,
    resumeKv: createMMKV({ id: "playerResumeCheckpoint" }),
    legacyPlayKv: createMMKV({ id: "streamEventQueue" }),
    auth: {
      currentUser: () => auth.currentUser,
      signOut: () => signOut(auth),
      subscribe: (listener) => auth.onAuthStateChanged(listener),
    },
    network: {
      isOnline: () => internetStatus.getCurrentIsOnline(),
      subscribe(listener) {
        internetStatus.subscribe(listener);
        return () => internetStatus.unsubscribe(listener);
      },
    },
    appState: {
      current: () => AppState.currentState,
      subscribe: (listener) => {
        const subscription = AppState.addEventListener("change", listener);
        return () => subscription.remove();
      },
    },
    platform: {
      os: Platform.OS,
      appVersion: config.APP_VERSION || "",
      buildNumber: config.APP_BUILD_NUMBER || "",
      updateId: config.UPDATES.ID || "",
      updateCreatedAt: config.UPDATES.CREATED_AT || "",
      runtimeVersion: config.UPDATES.RUNTIME_VERSION || "",
      countryCode: getCountryCode,
    },
  };
}
